import React, { useMemo, useState } from 'react';
import Spinner from '../../../components/common/spinner';
import { Paginator } from '../../../components/common/paginator';
import {
  BackgroundJob,
  useAbandonStaleBackgroundJobs,
  useBackgroundJobs,
  useBackgroundJobStats,
  useRetryBackgroundJob,
  useCancelBackgroundJob,
} from '../../../hooks/useBackgroundJobs';

const PAGE_LIMIT = 25;
const DEFAULT_STATUS_FILTER = 'pending,failed,running';

const STATUS_OPTIONS = [
  { value: '', label: 'All statuses' },
  { value: DEFAULT_STATUS_FILTER, label: 'Active' },
  { value: 'pending', label: 'Pending' },
  { value: 'running', label: 'Running' },
  { value: 'succeeded', label: 'Succeeded' },
  { value: 'failed', label: 'Failed' },
  { value: 'dead', label: 'Dead' },
  { value: 'cancelled', label: 'Cancelled' },
];

type StatsPeriod = 'this_week' | 'last_week' | 'month' | 'custom';

type StatusFilterCard = {
  label: string;
  status: string;
  count: number | string | undefined;
  filterable: boolean;
};

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const startOfLocalDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(0, 0, 0, 0);
  return next;
};

const endOfLocalDay = (date: Date) => {
  const next = new Date(date);
  next.setHours(23, 59, 59, 999);
  return next;
};

const startOfWeek = (date: Date) => {
  const next = startOfLocalDay(date);
  // Locale week starts on Sunday (matches existing switchboard availability UI).
  next.setDate(next.getDate() - next.getDay());
  return next;
};

const endOfWeek = (date: Date) => {
  const next = startOfWeek(date);
  next.setDate(next.getDate() + 6);
  return endOfLocalDay(next);
};

const resolvePeriodRange = (
  period: StatsPeriod,
  customStart: string,
  customEnd: string,
): { start_date: string; end_date: string } => {
  const now = new Date();

  if (period === 'this_week') {
    return {
      start_date: startOfWeek(now).toISOString(),
      end_date: endOfWeek(now).toISOString(),
    };
  }

  if (period === 'last_week') {
    const lastWeekAnchor = new Date(now);
    lastWeekAnchor.setDate(lastWeekAnchor.getDate() - 7);
    return {
      start_date: startOfWeek(lastWeekAnchor).toISOString(),
      end_date: endOfWeek(lastWeekAnchor).toISOString(),
    };
  }

  if (period === 'month') {
    const start = startOfLocalDay(new Date(now.getFullYear(), now.getMonth(), 1));
    const end = endOfLocalDay(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    return {
      start_date: start.toISOString(),
      end_date: end.toISOString(),
    };
  }

  const start = customStart ? startOfLocalDay(new Date(`${customStart}T00:00:00`)) : startOfWeek(now);
  const end = customEnd ? endOfLocalDay(new Date(`${customEnd}T00:00:00`)) : endOfWeek(now);
  return {
    start_date: start.toISOString(),
    end_date: end.toISOString(),
  };
};

const statusBadgeClass = (status: string) => {
  switch (status) {
    case 'running':
      return 'bg-blue-100 text-blue-700';
    case 'succeeded':
      return 'bg-green-100 text-green-700';
    case 'failed':
      return 'bg-amber-100 text-amber-800';
    case 'dead':
      return 'bg-red-100 text-red-700';
    case 'cancelled':
      return 'bg-gray-100 text-gray-600';
    default:
      return 'bg-gray-100 text-gray-700';
  }
};

const formatDate = (dateStr: string | null) => {
  if (!dateStr) return '—';
  const normalized = /Z$|[+-]\d\d:\d\d$/.test(dateStr) ? dateStr : `${dateStr}Z`;
  return new Date(normalized).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
};

const formatRefreshedAt = (timestamp: number | undefined) => {
  if (!timestamp) return '—';
  return new Date(timestamp).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    second: '2-digit',
  });
};

const formatDuration = (seconds: number | null) => {
  if (seconds == null) return '—';
  if (seconds < 60) return `${seconds.toFixed(1)}s`;
  return `${(seconds / 60).toFixed(1)}m`;
};

const formatJson = (value: unknown) => {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
};

const hasJobDetails = (job: BackgroundJob) =>
  Boolean(
    job.last_traceback ||
      job.last_error ||
      (job.args != null && job.args.length > 0) ||
      (job.kwargs && Object.keys(job.kwargs).length > 0),
  );

const BackgroundJobsTab: React.FC = () => {
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState(DEFAULT_STATUS_FILTER);
  const [executor, setExecutor] = useState('');
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);
  const [statsPeriod, setStatsPeriod] = useState<StatsPeriod>('this_week');
  const [customStart, setCustomStart] = useState(() => toDateInputValue(startOfWeek(new Date())));
  const [customEnd, setCustomEnd] = useState(() => toDateInputValue(endOfWeek(new Date())));

  const statsRange = useMemo(
    () => resolvePeriodRange(statsPeriod, customStart, customEnd),
    [statsPeriod, customStart, customEnd],
  );

  const {
    data,
    isLoading,
    refetch: refetchJobs,
    dataUpdatedAt: jobsUpdatedAt,
  } = useBackgroundJobs({
    page,
    limit: PAGE_LIMIT,
    status: status || undefined,
    executor: executor || undefined,
    q: search || undefined,
  });
  const { data: stats, refetch: refetchStats, dataUpdatedAt: statsUpdatedAt } = useBackgroundJobStats(statsRange);
  const { mutate: retryJob, isLoading: isRetrying } = useRetryBackgroundJob();
  const { mutate: cancelJob, isLoading: isCancelling } = useCancelBackgroundJob();
  const { mutate: abandonStale, isLoading: isAbandoning } = useAbandonStaleBackgroundJobs();

  const jobs = data?.data || [];
  const totalCount = data?.count || 0;
  const lastRefreshedAt = Math.max(jobsUpdatedAt || 0, statsUpdatedAt || 0) || undefined;

  const applySearch = () => {
    setSearch(searchInput.trim());
    setPage(1);
  };

  const refreshAll = () => {
    void refetchJobs();
    void refetchStats();
  };

  const canRetry = (job: BackgroundJob) => ['failed', 'dead', 'cancelled'].includes(job.status);
  // Allow cancel for running even when fired_at is set (zombie/stale Lambda claims).
  const canCancel = (job: BackgroundJob) => !['succeeded', 'cancelled'].includes(job.status);

  const requestCancel = (job: BackgroundJob) => {
    // Cancelling a live claim only updates the row: the worker keeps going, and
    // the scheduled-job overlap guard stops seeing it as in-flight.
    if (
      job.status === 'running' &&
      !window.confirm(
        `Cancel job #${job.id}?\n\nThis marks the row cancelled but does not stop work already running on the worker. ` +
          'It also releases the overlap guard, so the next scheduled tick or Run can start a second instance alongside it.',
      )
    ) {
      return;
    }
    cancelJob(job.id);
  };

  const clearStuckRunning = () => {
    if (
      !window.confirm(
        'Cancel all jobs that have been running for more than 60 minutes? This clears stuck claims; it cannot stop a live Lambda mid-flight, but the row will no longer show as running.',
      )
    ) {
      return;
    }
    abandonStale({ stale_after_minutes: 60 });
  };

  const applyStatusFilter = (nextStatus: string) => {
    setStatus((current) => (current === nextStatus ? DEFAULT_STATUS_FILTER : nextStatus));
    setPage(1);
  };

  const statusCards: StatusFilterCard[] = [
    { label: 'Pending', status: 'pending', count: stats?.pending_count, filterable: true },
    { label: 'Running', status: 'running', count: stats?.running_count, filterable: true },
    { label: 'Succeeded', status: 'succeeded', count: stats?.by_status?.succeeded, filterable: true },
    { label: 'Failed', status: 'failed', count: stats?.failed_count, filterable: true },
    { label: 'Dead', status: 'dead', count: stats?.dead_count, filterable: true },
    { label: 'Cancelled', status: 'cancelled', count: stats?.by_status?.cancelled, filterable: true },
    { label: 'SQS in flight', status: '', count: stats?.sqs_in_flight_count, filterable: false },
    {
      label: 'Oldest pending',
      status: '',
      count: stats?.oldest_pending_age_seconds != null ? formatDuration(stats.oldest_pending_age_seconds) : '—',
      filterable: false,
    },
  ];

  return (
    <div>
      <div className="flex flex-wrap items-center justify-between gap-3 border-b bg-gray-50 px-7 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold uppercase tracking-wide text-gray-500">Summary</span>
          <select
            value={statsPeriod}
            onChange={(e) => setStatsPeriod(e.target.value as StatsPeriod)}
            className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm"
            aria-label="Stats period"
          >
            <option value="this_week">This week</option>
            <option value="last_week">Last week</option>
            <option value="month">This month</option>
            <option value="custom">Custom</option>
          </select>
          {statsPeriod === 'custom' && (
            <>
              <input
                type="date"
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm"
                aria-label="Custom start date"
              />
              <span className="text-sm text-gray-500">to</span>
              <input
                type="date"
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="rounded border border-gray-300 bg-white px-2 py-1.5 text-sm"
                aria-label="Custom end date"
              />
            </>
          )}
        </div>
        <div className="text-xs text-gray-500">Status counts for jobs queued in the selected range</div>
      </div>

      <div className="grid grid-cols-2 gap-3 border-b bg-gray-50 px-7 py-4 sm:grid-cols-4 lg:grid-cols-8">
        {statusCards.map((card) => {
          const isActive = card.filterable && status === card.status;
          const className = `rounded border bg-white px-3 py-2 text-left ${
            card.filterable ? 'cursor-pointer hover:border-gray-400 hover:bg-gray-50' : ''
          } ${isActive ? 'border-gray-800 ring-1 ring-gray-800' : 'border-gray-200'}`;

          if (!card.filterable) {
            return (
              <div key={card.label} className={className}>
                <div className="text-xs uppercase tracking-wide text-gray-500">{card.label}</div>
                <div className="mt-1 text-lg font-semibold text-gray-900">{card.count ?? '—'}</div>
              </div>
            );
          }

          return (
            <button
              key={card.label}
              type="button"
              aria-pressed={isActive}
              onClick={() => applyStatusFilter(card.status)}
              className={className}
            >
              <div className="text-xs uppercase tracking-wide text-gray-500">{card.label}</div>
              <div className="mt-1 text-lg font-semibold text-gray-900">{card.count ?? '—'}</div>
            </button>
          );
        })}
      </div>

      <div className="flex flex-wrap items-center justify-between gap-3 border-b px-7 py-3">
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={status}
            onChange={(e) => {
              setStatus(e.target.value);
              setPage(1);
            }}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value || 'all'} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
          <select
            value={executor}
            onChange={(e) => {
              setExecutor(e.target.value);
              setPage(1);
            }}
            className="rounded border border-gray-300 px-2 py-1.5 text-sm"
          >
            <option value="">All executors</option>
            <option value="backend">Backend</option>
            <option value="lambda">Lambda</option>
          </select>
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && applySearch()}
            placeholder="Search module / error"
            className="w-56 rounded border border-gray-300 px-3 py-1.5 text-sm"
          />
          <button
            onClick={applySearch}
            className="rounded bg-gray-800 px-3 py-1.5 text-sm font-medium text-white hover:bg-gray-900"
          >
            Search
          </button>
        </div>
        <div className="flex items-center gap-3">
          <span className="text-xs text-gray-500">Last refreshed {formatRefreshedAt(lastRefreshedAt)}</span>
          <button
            onClick={clearStuckRunning}
            disabled={isAbandoning}
            className="rounded border border-amber-300 bg-amber-50 px-3 py-1.5 text-sm font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
          >
            {isAbandoning ? 'Clearing…' : 'Clear stuck running'}
          </button>
          <button
            onClick={refreshAll}
            className="rounded border border-gray-300 px-3 py-1.5 text-sm text-gray-600 hover:bg-gray-100"
          >
            Refresh
          </button>
        </div>
      </div>

      {isLoading && <Spinner />}

      {!isLoading && jobs.length === 0 && (
        <div className="flex items-center justify-center py-20 text-gray-400">No background jobs found</div>
      )}

      {!isLoading && jobs.length > 0 && (
        <>
          <div className="max-window-height-table overflow-x-auto overflow-y-auto border-b px-7">
            <table className="table-row-format relative w-full table-fixed border-collapse">
              <thead className="sticky top-0 bg-white">
                <tr className="border-b text-left text-xs font-semibold uppercase tracking-wider text-gray-500">
                  <th className="w-16 py-3 pr-2">ID</th>
                  <th className="py-3 pr-2">Handler</th>
                  <th className="w-24 py-3 pr-2">Status</th>
                  <th className="w-20 py-3 pr-2">Executor</th>
                  <th className="w-16 py-3 pr-2">Pri</th>
                  <th className="w-16 py-3 pr-2">Tries</th>
                  <th className="w-20 py-3 pr-2">Duration</th>
                  <th className="w-36 py-3 pr-2">Queued</th>
                  <th className="w-40 py-3 pr-2 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((job) => (
                  <React.Fragment key={job.id}>
                    <tr className="border-b text-sm hover:bg-gray-50">
                      <td className="py-3 pr-2 font-mono text-xs">{job.id}</td>
                      <td className="truncate py-3 pr-2">
                        <div className="truncate font-medium" title={`${job.module}.${job.function}`}>
                          {job.module}.{job.function}
                        </div>
                        {job.last_error && (
                          <div className="truncate text-xs text-red-600" title={job.last_error}>
                            {job.last_error}
                          </div>
                        )}
                      </td>
                      <td className="py-3 pr-2">
                        <button
                          type="button"
                          onClick={() => applyStatusFilter(job.status)}
                          className={`inline-block rounded px-2 py-0.5 text-xs font-medium ${statusBadgeClass(
                            job.status,
                          )}`}
                        >
                          {job.status}
                        </button>
                      </td>
                      <td className="py-3 pr-2 text-xs text-gray-600">{job.executor || '—'}</td>
                      <td className="py-3 pr-2 text-xs">{job.priority}</td>
                      <td className="py-3 pr-2 text-xs">
                        {job.attempts}/{job.max_attempts}
                      </td>
                      <td className="py-3 pr-2 text-xs">{formatDuration(job.duration_seconds)}</td>
                      <td className="py-3 pr-2 text-xs text-gray-500">{formatDate(job.queued_at)}</td>
                      <td className="py-3 pr-2 text-right">
                        <div className="flex items-center justify-end gap-1">
                          {hasJobDetails(job) && (
                            <button
                              onClick={() => setExpandedId(expandedId === job.id ? null : job.id)}
                              className="rounded px-2 py-1 text-xs font-medium text-gray-600 hover:bg-gray-100"
                            >
                              {expandedId === job.id ? 'Hide' : 'Details'}
                            </button>
                          )}
                          {canRetry(job) && (
                            <button
                              onClick={() => retryJob(job.id)}
                              disabled={isRetrying}
                              className="rounded px-2 py-1 text-xs font-medium text-amber-600 hover:bg-amber-50 disabled:opacity-50"
                            >
                              Retry
                            </button>
                          )}
                          {canCancel(job) && (
                            <button
                              onClick={() => requestCancel(job)}
                              disabled={isCancelling}
                              className="rounded px-2 py-1 text-xs font-medium text-red-600 hover:bg-red-50 disabled:opacity-50"
                            >
                              Cancel
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                    {expandedId === job.id && (
                      <tr className="border-b bg-gray-50">
                        <td colSpan={9} className="px-3 py-3">
                          <div className="text-xs text-gray-600">
                            <div>
                              <span className="font-semibold">Claimed by:</span> {job.claimed_by || '—'}
                            </div>
                            <div>
                              <span className="font-semibold">SQS queued:</span> {formatDate(job.queued_to_sqs_at)}
                            </div>
                            {(job.args != null && job.args.length > 0) ||
                            (job.kwargs && Object.keys(job.kwargs).length > 0) ? (
                              <div className="mt-2 grid gap-2 sm:grid-cols-2">
                                {job.args != null && job.args.length > 0 && (
                                  <div>
                                    <div className="font-semibold">Args</div>
                                    <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[10px]">
                                      {formatJson(job.args)}
                                    </pre>
                                  </div>
                                )}
                                {job.kwargs && Object.keys(job.kwargs).length > 0 && (
                                  <div>
                                    <div className="font-semibold">Kwargs</div>
                                    <pre className="mt-1 max-h-32 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[10px]">
                                      {formatJson(job.kwargs)}
                                    </pre>
                                  </div>
                                )}
                              </div>
                            ) : null}
                            {job.last_traceback && (
                              <pre className="mt-2 max-h-48 overflow-auto whitespace-pre-wrap rounded bg-white p-2 text-[10px]">
                                {job.last_traceback}
                              </pre>
                            )}
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
          <Paginator count={totalCount} limit={PAGE_LIMIT} currentPage={page} handlePageChange={setPage} />
        </>
      )}
    </div>
  );
};

export default BackgroundJobsTab;
